import { UntypedFormGroup } from '@angular/forms';
import { Injectable } from '@angular/core';
import { of, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiService } from '../api.service';
import { TranslateService } from './translate.service';
import memoizeObservable from '../utils/memoize-observable';
import { TransactionType, TransactionTypes } from '../models/transactions-enum';
import { Countries } from '../utils/countries-enum';
import { DataObject } from '../interfaces/data-objects';
import { camelCase, upperFirst } from 'lodash';

@Injectable({
  providedIn: 'root'
})
export class FormService {


  removePropertyBuyerSubject = new Subject<any>();
  associatePropertyBuyerSubject = new Subject<any>();
  getPropertyBuyerSubject = new Subject<any>();

  private sizeMeasurementOptions = [
    { value: 'SQM', label: 'SQM' },
    { value: 'SQFT', label: 'SQFT' }
  ];

  private landMeasurementOptions = [
    { value: 'hectares', label: 'Hectares' },
    { value: 'acres', label: 'Acres' },
    { value: 'SQM', label: 'SQM' }
  ];

  private loadedForms: DataObject = {};

  // Memoized observable methods
  getFormFields = memoizeObservable((form, fieldName, fieldValue) => {
    return this.api.get(`/api/form/form_fields/?form=${form}&${fieldName}=${fieldValue}`)
      .pipe(
        map(response => {
          return response.field_list;
        })
      );
  });

  getFieldOptions = memoizeObservable((model: string, field: string, country: string = null, query: string = null) => {

    if (field === 'communal_space_measurement') {
      return of(this.sizeMeasurementOptions);
    }

    if (field === 'both') {
      return of(this.sizeMeasurementOptions.concat(this.landMeasurementOptions
        .filter(measurement => measurement.value !== 'acres' && measurement.value !== 'SQM')));
    }

    return this.api.get(`/api/form/field_options/?model=${model}&field=${field}&country=${country}&q=${query}`)
      .pipe(
        map(response => {
          return response.options;
        })
      );
  });

  getFieldOptionsWithTransType = memoizeObservable((model: string, field: string, transaction_type: string = null) => {
    return this.api.get(`/api/form/field_options/?model=${model}&field=${field}&trans_type=${transaction_type}`)
      .pipe(map(response => response.options));
  });

  getSubtypeFieldOptions = memoizeObservable((model: string, field: string, propertyType: string, transactionType: string) => {
    const params = `model=${model}&field=${field}&property_type=${propertyType}&trans_type=${transactionType}`;
    return this.api.get(`/api/form/field_options/?${params}`)
    .pipe(
      map(response => {
        return response.options;
      })
    );
  });

  getCurrencies = memoizeObservable(() => {
    return this.api.get(`/api/form/currencies/`)
    .pipe(
      map(response => {
        return Object.keys(response).map(currency => response[currency]);
      })
    );
  });

  getGSTRates = memoizeObservable(() => {
    return this.api.get(`/api/form/gstrates/`)
      .pipe(
        map(response => {
          return response.result.gst_types;
        })
      );
  });

  getManagingOffices = memoizeObservable((query) => {
    return this.api.get(`/api/ps/managing-offices/?q=${query}`)
    .pipe(
      map(response => {
        return response.results;
      })
    );
  });

  getManagingOffice = memoizeObservable((id) => {
    return this.api.get(`/api/ps/managing-offices/?id=${id}`);
  });

  getProjectCodes = memoizeObservable((query) => {
    return this.api.get(`/api/ps/projects/?q=${query}`)
      .pipe(
        map(response => {
          return response.results;
        })
      );
  });

  getProjectCode = memoizeObservable((id) => {
    return this.api.get(`/api/ps/projects/?id=${id}`);
  });

  getBillingEntities = memoizeObservable((country) => {
    return this.api.get(`/api/form/billing_entities/?country=${country}`)
    .pipe(
      map(response => {
        return response.options;
      })
    );
  });

  getPSBillingEntities = memoizeObservable(() => {
    return this.api.get(`/api/ps/billing-entities/`)
    .pipe(
      map(response => {
        return response.results;
      })
    );
  });

  getActivity = memoizeObservable((projectId, id) => {
    return this.api.get(`/api/ps/activities/?project=${projectId}&id=${id}`);
  });

  getActivitiesByProjectId = memoizeObservable((projectId) => {
    if (!projectId) {
      return of([]);
    }
    return this.api.get(`/api/ps/activities/?project=${projectId}`)
      .pipe(
        map(response => {
          return response.results;
        })
      );
  });

  searchActivitiesByProjectId = memoizeObservable((projectId, query) => {
    return this.api.get(`/api/ps/activities/?project=${projectId}&q=${encodeURIComponent(query)}`)
    .pipe(
      map(response => {
        return response.results;
      })
    );
  });

  getSourceCodes = memoizeObservable((query: string, billing_entity = '') => {
    return this.api.get(`/api/ps/source-codes/?q=${encodeURIComponent(query)}&billing_entity=${encodeURIComponent(billing_entity)}`)
    .pipe(
      map(response => { return response.results; })
    );
  });

  getSourceCode = memoizeObservable((id) => {
    return this.api.get(`/api/ps/source-codes/?id=${id}`);
  });

  getCategoriesBySourceCodeId = memoizeObservable((sourceCodeId) => {
    if (!sourceCodeId) {
      return of([]);
    }
    return this.api.get(`/api/ps/categories/?source_code=${sourceCodeId}`)
      .pipe(
        map(response => {
          return response.results;
        })
      );
  });

  // Defaults to Split service types
  getServiceTypes = memoizeObservable((billingCurrency) => {
    return this.api.get(`/api/ps/service-types/?curr=${billingCurrency}&page_size=100`)
      .pipe(
        map(response => {
          return response.results;
        })
      );
  });

  getServiceTypesByService = memoizeObservable((service, billingCurrency) => {
    return this.api.get(`/api/ps/service-types/?service=${service}&curr=${billingCurrency}&page_size=100`)
    .pipe(
      map(response => {
        return response.results;
      })
    );
  });

  getCustomerId = memoizeObservable((query) => {
    return this.api.get(`/api/ps/contacts/?name=${query}`)
    .pipe(
      map(response => {
        return response.results;
      })
    );
  });

  getAffiliateCodeByCustomerId = memoizeObservable((query) => {
    return this.api.get(`/api/ps/customers/?id=${query}`)
    .pipe(
      map(response => {
        return response.results;
      })
    );
  });

  getApprovers = memoizeObservable((query, billing_entity, my_finance) => {
    if (!query) {
      query = '';
    }
    const queryParams = my_finance === 'peoplesoft' ? `q=${query}&billing_entity=${billing_entity}`
      : `q=${query}`
    return this.api.get(`/api/ps/approvers/?${queryParams}`);
  });

  getApproverById = memoizeObservable((approverId) => {
    return this.api.get(`/api/ps/approvers/?id=${approverId}`);
  });

  getAdjustmentReasons = memoizeObservable(() => {
    return this.api.get(`/api/form/field_options/?model=REBILL_ADJUSTMENT&field=REASON_TYPE`)
      .pipe(
        map(response => {
          if (response && response.options) {
            return response.options;
        }})
      );
  });

  getExecutiveApprovers = memoizeObservable((query: any) => {
    if (!query) {
      query = '';
    }
    return this.api.get(`/api/ps/executive_approvers/?q=${query}`)
    .pipe(
      map((data) => data?.results)
    );
  });

  getConfidentialInformation = memoizeObservable((transactionType: string) => {
    const params = `model=transaction&field=confidential&trans_type=${transactionType}`;
    return this.api.get(`/api/form/field_options/?${params}`).pipe(map(response => response.options));
  });

  getSupplierTypes = memoizeObservable(() => {
    return this.api.get(`/api/transactions/suppliertype`)
      .pipe(
        map(response => {
          if (response && response.results) {
            return response.results;
          }
        })
      );
  });

  getSGSTInfo = memoizeObservable(() => {
    return this.api.get(`/api/transactions/taxinfov1/`)
    .pipe(
      map(response => {
        return response.results;
      })
    );
  });

  getCGSTInfo = memoizeObservable(() => {
    return this.api.get(`/api/transactions/taxinfov2/`)
    .pipe(
      map(response => {
        return response.results;
      })
    );
  });

  getIGSTInfo = memoizeObservable(() => {
    return this.api.get(`/api/transactions/taxinfov3/`)
    .pipe(
      map(response => {
        return response.results;
      })
    );
  });

  getCESSInfo = memoizeObservable(() => {
    return this.api.get(`/api/transactions/taxinfov4/`)
    .pipe(
      map(response => {
        return response.results;
      })
    );
  });

  getBillingOffice = memoizeObservable((query) => {
    return this.api.get(`/api/transactions/billing_office/?q=${encodeURIComponent(query)}`)
    .pipe(
      map(response => {
        return response.options;
      })
    );
  });

  getPlaceOfSupply = memoizeObservable((query) => {
    return this.api.get(`/api/transactions/place_of_supply/?q=${encodeURIComponent(query)}`)
    .pipe(
      map(response => {
        return response.options;
      })
    );
  });

  getPaymentTerms = memoizeObservable(() => {
    return this.api.get(`/api/transactions/payment_terms/`)
    .pipe(
      map(response => {
        return response.results;
      })
    );
  });

  getInvoiceHeaderOptions = memoizeObservable((transactionType) => {
    return this.api.get(`/api/transactions/invoice_header/?transaction_type=${transactionType}`)
      .pipe(
        map(response => {
          return response.options;
        })
      );
  });

  getGSTRegistrationTypes = memoizeObservable(() => {
    return this.api.get(`/api/customer/gst_registration_type/`)
    .pipe(
      map(response => response.options)
    );
  });

  getGwsLocations = memoizeObservable((country: string = '') => {
    return this.api.get(`/api/form/gws_locations/?country=${country}`)
    .pipe(
      map(response => response.options)
    );
  });

  getEdpAvailabilities = memoizeObservable(
    (propertyId: number, propertyType: string, availabilityType: 'floor' | 'suite', transactionType: string, floorNumber?: string) => {
      let params = `lease_comp_property_id=${propertyId}&availability_type=${availabilityType}&property_type=${propertyType}&transaction_type=${transactionType}`;

      if (floorNumber) {
        params += `&floor_number=${floorNumber}`;
      }

      return this.api.get(`/api/transactions/availabilities_property_details/?${params}`)
        .pipe(map(response => response.options));
    });

  getEdpPropertyType = memoizeObservable((propertyId: number, transactionType: string) => {
    let params = `lease_comp_property_id=${propertyId}`;

    if (transactionType) {
      params += `&transaction_type=${transactionType}`;
    }

    return this.api.get(`/api/transactions/availabilities_property_details/?${params}`)
      .pipe(map(response => response));
  });

  getTaxRateCodes = memoizeObservable((billingOffice = null) => {
    return this.api.get(`/api/form/get_tax_rate_code/?billing_office=${billingOffice}`)
    .pipe(
      map(response => response.options)
    );
  });

  getExciseTaxRateCodes = memoizeObservable(() => {
    return this.api.get(`/api/form/get_excise_tax_rate/`)
    .pipe(
      map(response => response.options)
    );
  });

  getCountries = memoizeObservable(() => {
    return this.api.get(`/api/form/countries/`).pipe(map((response: any) => {
      let list = [];
      const countries = response['countries'];
      countries.forEach(ctry => {
        list.push({ 'value': ctry.label, 'label': ctry.label });
      });
      return list;
    }));
  });

  constructor(private api: ApiService, private translateService: TranslateService) { }

  showFormValidations(formGroup: UntypedFormGroup) {
    Object.keys(formGroup.controls).forEach(field => {
      const control = formGroup.get(field);
      control?.markAsTouched({ onlySelf: true });
      control?.markAsDirty({ onlySelf: true });
    });
  }

  clearPageForms() {
    this.loadedForms = {};
  }

  loadedFormsAreInvalid() {
    // temporarily hide the validation modal
    return false;
    // return Object.keys(this.loadedForms).some((formKey, index) => {
    //   return this.loadedForms[formKey].invalid;
    // });
  }

  addLoadedForm(form: UntypedFormGroup, uuid: string) {
    this.loadedForms[uuid] = form;
  }

  showLoadedFormsValidations() {
    Object.keys(this.loadedForms).map(formKey => {
      this.showFormValidations(this.loadedForms[formKey]);
    });
  }

  checkDups(arrOfObjs) {
    if (arrOfObjs && arrOfObjs.length > 1) {
      const valueArr = arrOfObjs.map(function (item) {
        return item.expense_service_type;
      });
      const newArr = [];
      for (let count = 0; count < valueArr.length; count++) {
        if (valueArr[count]) {
          newArr.push(valueArr[count]);
        }
      }
      return newArr.some(function (item, idx) {
        return newArr.indexOf(item, idx + 1) !== -1;
      });
    } else {
      return false;
    }
  }

  // Ensure that there are no more than 3 digits before the decimal point
  isPercentFieldValid(value) {
    let integerDigits, returnValue;
    value = value.toString();
    integerDigits = (value.includes('.')) ? value.split('.')[0] : value;
    integerDigits.length > 3 ? returnValue = false : returnValue = true;
    return returnValue;
  }

  getSwalOptions(options) {
    let flag = true;
    if (options) {
      if (options.allowOutsideClick === null || options.allowOutsideClick === undefined) {
        flag = true;
      } else if (options.allowOutsideClick === false) {
        flag = false;
      }
    }
    return {
      showConfirmButton: options.showConfirmButton,
      showCloseButton: options.showCloseButton,
      confirmButtonText: this.translateService.getTranslation(`${options.confirmButtonText}`),
      cancelButtonText: this.translateService.getTranslation(`${options.cancelButtonText}`),
      showCancelButton: options.showCancelButton,
      allowOutsideClick: flag,
      confirmButtonColor: options.showConfirmButton ? '#00a657' : null,
      cancelButtonColor: options.showCancelButton ? '#747474' : null
    };
  }

  getPaymentDueWarningText(transactionType: TransactionType, userCountry: string) {
    let warningText = '';
    switch (userCountry) {
      case Countries.India:
      case Countries.Australia:
      case Countries.NewZealand:
        switch (transactionType) {
          case TransactionTypes.LEASE:
          case TransactionTypes.SALE:
          case TransactionTypes.CONSULTING:
          case TransactionTypes.DSF:
            warningText = this.translateService.getTranslation('UPDATE_CONTRACTUAL_DATE_60');
            break;
          case TransactionTypes.EXPENSE:
            warningText = this.translateService.getTranslation('UPDATE_CONTRACTUAL_DATE_30');
            break;
        }
        break;
      case Countries.China:
      case Countries.Singapore:
      case Countries.Hongkong:
      case Countries.Indonesia:
        switch (transactionType) {
          case TransactionTypes.LEASE:
          case TransactionTypes.CONSULTING:
          case TransactionTypes.DSF:
            warningText = this.translateService.getTranslation('UPDATE_CONTRACTUAL_DATE_60');
            break;
          case TransactionTypes.SALE:
            warningText = this.translateService.getTranslation('UPDATE_CONTRACTUAL_DATE_90');
            break;
          case TransactionTypes.EXPENSE:
            warningText = this.translateService.getTranslation('UPDATE_CONTRACTUAL_DATE_30');
            break;
        }
        break;
      case Countries.Japan:
        switch (transactionType) {
          case TransactionTypes.LEASE:
          case TransactionTypes.SALE:
          case TransactionTypes.CONSULTING:
          case TransactionTypes.DSF:
            warningText = this.translateService.getTranslation('UPDATE_CONTRACTUAL_DATE_180');
            break;
          case TransactionTypes.EXPENSE:
            warningText = this.translateService.getTranslation('UPDATE_CONTRACTUAL_DATE_90');
            break;
        }
        break;
      case Countries.Thailand:
      case Countries.Philippines:
      case Countries.Vietnam:
      case Countries.Korea:
        warningText = this.translateService.getTranslation('UPDATE_CONTRACTUAL_DATE_30');
        break;
      case Countries.Taiwan:
        warningText = this.translateService.getTranslation('UPDATE_CONTRACTUAL_DATE_60');
        break;
      default:
        break;
    }
    return warningText;
  }

  getGSTLabel() {
    const country = window.localStorage.getItem('country');
    switch (country) {
      case Countries.Australia:
      case Countries.India:
      case Countries.NewZealand:
      case Countries.Hongkong:
      case Countries.Singapore:
      case Countries.Indonesia:
        return this.translateService.getTranslation('GST_PERCENT');
      case Countries.Thailand:
      case Countries.Philippines:
      case Countries.Vietnam:
      case Countries.Korea:
      case Countries.Taiwan:
        return this.translateService.getTranslation('VAT_PERCENT');
      case Countries.Japan:
      case Countries.China:
        return this.translateService.getTranslation('TAX_PERCENT');
      default:
        break;
    }
  }

  getGSTFeeLabel() {
    const country = window.localStorage.getItem('country');
    switch (country) {
      case Countries.Australia:
      case Countries.India:
      case Countries.NewZealand:
      case Countries.Hongkong:
      case Countries.Singapore:
        return this.translateService.getTranslation('GST_FEE');
      case Countries.Thailand:
      case Countries.Philippines:
      case Countries.Vietnam:
      case Countries.Korea:
      case Countries.Taiwan:
        return this.translateService.getTranslation('VAT_FEE');
      case Countries.Japan:
      case Countries.China:
        return this.translateService.getTranslation('TAX_FEE');
      default:
        break;
    }
  }

  searchPropertyForBuyer(transactionId, params, trans_type) {
    let url = `/api/transactions/`;

    if (trans_type === 'sale') {
      url += `sales/${transactionId}/get_properties`;
    }

    if (trans_type === 'lease') {
      url += `leases/${transactionId}/get_properties_lease`;
    }
    return this.api.get(`${url}/?q=${params}`)
      .pipe(map(response => {
        return response;
      }));
  }

  /*
  * INFO: Returns the size measurment based on user's country
  * */
  getSizeMeasurement(userCountry: string): string {
    switch (userCountry) {
      case Countries.Hongkong:
      case Countries.Singapore:
      case Countries.India:
      case Countries.China:
      case Countries.Indonesia:
        return 'SQFT';
      case Countries.Taiwan:
        return 'Ping';
      case Countries.Japan:
        return 'Tsubo';
      case Countries.Thailand:
      case Countries.Philippines:
      case Countries.Vietnam:
      case Countries.Korea:
        return 'Pyeong';
      default:
        return 'SQM';
    }
  }

  /*
  * INFO: Returns the land measurment based on user's country and transaction type
  * */
  getLandMeasurement(userCountry: string, transactionType?: string): string {
    switch (userCountry) {
      case Countries.India:
      case Countries.China:
      case Countries.Taiwan:
        return 'acres';
      case Countries.Singapore:
        if (transactionType && transactionType === TransactionTypes.VALUATION) {
          return 'SQM';
        }
        return 'hectares';
      default:
        return 'hectares';
    }
  }

  getInvoiceFormOptions = memoizeObservable(() => {
    return this.api.get(`/api/form/field_options/?model=transaction&field=invoice_form`)
      .pipe(map(response => response.options));
  });

  getOfficeOptions = memoizeObservable((query) => {
    return this.api.get(`/api/ps/locations/?q=${query}`)
    .pipe(
      map(response => response.options)
    );
  });

  getRepRoleOptions = memoizeObservable(() => {
    return this.api.get(`/api/form/field_options/?model=sale&field=bill_to_cep_reprole`)
      .pipe(map(response => response.options));
  });

  getFormModel(type: string, transactionType: string, userCountry: string) {
    const prefix = `${upperFirst(camelCase(type))}`;
    if (userCountry === 'SGP' && transactionType === 'valuation') {
      return `${prefix}FormValuationSGP`;
    }
    if (transactionType === 'lease') {
      return `${prefix}LeaseForm`;
    }
    if (transactionType === 'sale' && type === 'data_center') {
      return `${prefix}SaleForm`;
    }
    return `${prefix}Form`;
  }
}
